/**
 * DrawdownPage — Retirement Drawdown Strategy Planner
 *
 * Answers "When I actually retire, in what order do I pull from my pots,
 * and can I survive a crash at the start?".
 *
 * Shows:
 *  - Tax-Optimised Drawdown Blend (new section, top)
 *  - Three bucket cards (cash / bonds / growth) with target, current, fill
 *  - Tax-efficient withdrawal sequence as a numbered timeline
 *  - Crash test verdict (can bucket 1+2 ride out a 7-year equity drawdown?)
 *  - Recommendations list
 *  - Methodology footer
 */
import { useEffect, useRef, useState } from 'react'
import {
  Wallet, Layers, Shuffle, AlertTriangle, ShieldCheck, Sparkles,
  RefreshCw, ListChecks, Info, Zap, Globe, TrendingUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import ScenariosTabs from '@/components/ui/ScenariosTabs'
import { usePlanningAssumptions } from '@/hooks/usePlanningAssumptions'
import ScenarioDisclaimerFooter from '@/components/ScenarioDisclaimerFooter'
import EditableNumberSlider from '@/components/ui/EditableNumberSlider'
import { fmtGBP, fmtGBPCompact, fmtPct } from '@/lib/format'
import {
  ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts'
import { gridStroke, axisTickFill, tooltipContentStyle, legendWrapperStyle } from '@/lib/chartTheme'

interface BucketCard {
  label: string
  years_covered: number
  target_gbp: number
  current_gbp: number
  gap_gbp: number
  surplus_gbp: number
  fill_pct: number
  purpose: string
}

interface SequencePhase {
  phase: number
  age_range: string
  source: string
  why: string
  est_amount_gbp: number
  est_tax_gbp: number
}

interface CrashTest {
  scenario: string
  defensive_pot_gbp: number
  growth_pot_pre_crash_gbp: number
  growth_pot_post_crash_gbp: number
  years_covered_by_defensive: number
  equity_recovery_years_needed: number
  passes: boolean
  verdict: string
}

interface FxStressInfo {
  applied_pct: number
  foreign_share?: number
  foreign_exposure_gbp?: number
  pot_before?: number
  pot_after?: number
  impact_gbp?: number
  note?: string
  error?: string
}

interface DrawdownStrategy {
  headline: string
  current_pot_gbp: number
  target_required_gbp: number
  years_to_retirement: number
  years_in_retirement: number
  total_lifetime_spend_needed_gbp: number
  wrapper_totals_gbp: Record<string, number>
  tax_efficient_sequence: SequencePhase[]
  bucket_allocation: {
    bucket_1_cash: BucketCard
    bucket_2_bonds: BucketCard
    bucket_3_growth: BucketCard
  }
  current_bucket_split: {
    cash_gbp: number
    bond_gbp: number
    growth_gbp: number
    cash_pct: number
    bond_pct: number
    growth_pct: number
  }
  crash_test: CrashTest
  fx_stress?: FxStressInfo
  recommendations: string[]
  assumptions: Record<string, any>
}

// ── Projection types ─────────────────────────────────────────────────────────
interface ProjectionYear {
  year: number
  age: number
  phase: 'accumulating' | 'drawdown'
  target_gross_gbp: number
  gross_received_gbp: number
  net_received_gbp: number
  total_tax_gbp: number
  effective_tax_rate_pct: number
  sipp_balance_gbp: number
  isa_balance_gbp: number
  gia_balance_gbp: number
  pcls_remaining_gbp: number
  sources?: {
    state_pension_gbp: number
    db_pension_gbp: number
    gia_cgt_free_gbp: number
    isa_gbp: number
    pcls_tax_free_gbp: number
    sipp_basic_rate_gbp: number
    sipp_higher_rate_gbp: number
    extra_gia_gbp: number
  }
  tax_breakdown?: {
    income_tax_gbp: number
    cgt_gbp: number
  }
}

// ── Tax-optimised blend types ────────────────────────────────────────────────
interface BlendSources {
  state_pension_gbp: number
  db_pension_gbp: number
  gia_cgt_free_gbp: number
  isa_gbp: number
  pcls_tax_free_gbp: number
  sipp_basic_rate_gbp: number
  sipp_higher_rate_gbp: number
  extra_gia_gbp: number
}

interface OptimiseResponse {
  target_gross_gbp: number
  target_net_gbp: number
  starting_balances: {
    sipp_gbp: number
    isa_gbp: number
    gia_gbp: number
    pcls_gbp: number
  }
  // Null when retirement falls beyond the projection window (no drawdown
  // year exists inside years_to_project) — the backend emits None.
  first_retire_year: {
    gross_received_gbp: number
    net_received_gbp: number
    total_tax_gbp: number
    effective_tax_rate_pct: number
    sources: BlendSources
    tax_breakdown: {
      income_tax_gbp: number
      cgt_gbp: number
    }
  } | null
  comparison_vs_naive_all_sipp: {
    naive_net_gbp: number
    naive_tax_gbp: number
    optimal_net_gbp: number
    savings_per_year_gbp: number
    savings_per_decade_gbp: number
  }
  projection: ProjectionYear[]
}

const SOURCE_LABELS: Record<keyof BlendSources, string> = {
  state_pension_gbp:    'State Pension',
  db_pension_gbp:       'DB Pension',
  gia_cgt_free_gbp:     'GIA (CGT-free)',
  isa_gbp:              'ISA',
  pcls_tax_free_gbp:    'PCLS (tax-free)',
  sipp_basic_rate_gbp:  'SIPP (basic rate)',
  sipp_higher_rate_gbp: 'SIPP (higher rate)',
  extra_gia_gbp:        'Extra GIA',
}

const SOURCE_COLOURS: Record<keyof BlendSources, string> = {
  state_pension_gbp:    'bg-blue',
  db_pension_gbp:       'bg-blue/60',
  gia_cgt_free_gbp:     'bg-emerald',
  isa_gbp:              'bg-emerald/60',
  pcls_tax_free_gbp:    'bg-amber',
  sipp_basic_rate_gbp:  'bg-amber/70',
  sipp_higher_rate_gbp: 'bg-rose/70',
  extra_gia_gbp:        'bg-surface-hover border border-border',
}

// ─────────────────────────────────────────────────────────────────────────────

export default function DrawdownPage() {
  const [spend, setSpend] = useState(220_000)
  const [age, setAge] = useState(57)
  const [retireAge, setRetireAge] = useState(60)
  const [statePensionAge, setStatePensionAge] = useState(67)
  const [swr, setSwr] = useState(3.5)
  const [includeSP, setIncludeSP] = useState(true)

  const [fxStressPct, setFxStressPct] = useState(0)

  const [data, setData] = useState<DrawdownStrategy | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Phase A — shared planning assumptions; sliders write through the
  // store so MC / FIRE / scenarios see the change next tick.
  const assumptions = usePlanningAssumptions((s) => s.assumptions)
  const fetchAssumptions = usePlanningAssumptions((s) => s.fetch)
  const patchDebounced = usePlanningAssumptions((s) => s.patchDebounced)
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (!assumptions) {
      void fetchAssumptions()
      return
    }
    if (!hydratedRef.current) {
      hydratedRef.current = true
      setSpend(assumptions.target_drawdown_gbp ?? 220_000)
      setSwr(assumptions.swr_pct)
      setAge(assumptions.current_age)
      setRetireAge(assumptions.retirement_age)
      setIncludeSP(assumptions.include_state_pension)
    }
  }, [assumptions, fetchAssumptions])

  // Tax-optimised blend state
  const [blend, setBlend] = useState<OptimiseResponse | null>(null)
  const [blendLoading, setBlendLoading] = useState(false)
  const [blendError, setBlendError] = useState<string | null>(null)
  const [savingTarget, setSavingTarget] = useState(false)
  const [savedTarget, setSavedTarget] = useState(false)
  const [saveTargetError, setSaveTargetError] = useState<string | null>(null)

  const fetchBlend = async () => {
    setBlendLoading(true)
    setBlendError(null)
    try {
      const res = await api.post<OptimiseResponse>('/api/drawdown/optimise', {
        target_gbp: spend,
        target_is_net: false,
        current_age: age,
        retirement_age: retireAge,
        state_pension_age: statePensionAge,
        joint: true,
        years_to_project: 35,
      })
      setBlend(res.data)
    } catch (err: any) {
      setBlendError(err?.message ?? 'Failed to load optimised blend')
    } finally {
      setBlendLoading(false)
    }
  }

  const saveHouseholdTarget = async () => {
    setSavingTarget(true)
    setSaveTargetError(null)
    try {
      // Backend TargetUpdate contract: { target_drawdown_gbp: number > 0 }
      await api.put('/api/drawdown/target', { target_drawdown_gbp: spend })
      setSavedTarget(true)
      setTimeout(() => setSavedTarget(false), 3000)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setSaveTargetError(
        typeof detail === 'string'
          ? detail
          : (err?.message ?? 'Failed to save household target'),
      )
    } finally {
      setSavingTarget(false)
    }
  }

  const fetchStrategy = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<DrawdownStrategy>('/api/drawdown/strategy', {
        params: {
          annual_spend: spend,
          current_age: age,
          retirement_age: retireAge,
          state_pension_age: statePensionAge,
          swr,
          include_state_pension: includeSP,
          ...(fxStressPct !== 0 ? { fx_stress_pct: fxStressPct } : {}),
        },
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load drawdown strategy')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStrategy()
    fetchBlend()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(fetchStrategy, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spend, age, retireAge, statePensionAge, swr, includeSP, fxStressPct])

  useEffect(() => {
    const t = setTimeout(fetchBlend, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spend, age, retireAge, statePensionAge])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Layers size={20} className="text-blue-light" aria-hidden />
            Retirement Drawdown Strategy
          </h1>
          <p className="text-sm text-text-secondary">
            Sequence, buckets, and crash test for your withdrawal plan.
          </p>
        </div>
        <button type="button" onClick={fetchStrategy} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <ScenariosTabs />

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* ── Tax-Optimised Drawdown Blend ─────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="panel-title flex items-center gap-2">
            <Zap size={14} className="text-amber" aria-hidden />
            Tax-Optimised Drawdown Blend
          </h2>
          <div className="flex items-center gap-2">
            <Link
              to="/app/jurisdictions"
              className="text-xs text-blue-light hover:underline flex items-center gap-1"
            >
              <Globe size={12} aria-hidden />
              Compare jurisdictions →
            </Link>
            <button
              type="button"
              onClick={fetchBlend}
              className="btn-secondary gap-2 text-xs"
            >
              <RefreshCw size={12} className={blendLoading ? 'animate-spin' : ''} aria-hidden />
              Refresh
            </button>
          </div>
        </div>

        {blendError && (
          <div className="card p-3 border-rose/30 bg-rose/5 text-xs text-rose-light mb-3">
            {blendError}
          </div>
        )}

        {blendLoading && !blend && (
          <div className="space-y-3">
            <div className="h-24 skeleton rounded-xl" />
            <div className="h-20 skeleton rounded-xl" />
          </div>
        )}

        {/* Honest guard — retirement beyond the projection window means the
            backend has no drawdown year to blend (first_retire_year is null). */}
        {blend && blend.first_retire_year == null && (
          <div className="card p-4 border-amber/30 bg-amber/5 flex items-start gap-3">
            <Info size={14} className="text-amber shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-text-secondary">
              Retirement at age {retireAge} falls beyond the projection window
              starting from age {age}, so there is no drawdown year to blend yet.
              Bring the retirement age closer (or lower the current-age gap) to
              see the tax-optimised blend.
            </p>
          </div>
        )}

        {blend && blend.first_retire_year != null && (
          <div className="space-y-4">
            {/* Big summary card */}
            <div className="card p-5 border-2 border-amber/30 bg-amber/5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm stagger-children">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Target Gross</p>
                  <p className="text-xl font-bold text-text-primary mt-1">
                    {fmtGBP(blend.first_retire_year.gross_received_gbp)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Optimal Net</p>
                  <p className="stat-hero text-emerald-light mt-1">
                    {fmtGBP(blend.first_retire_year.net_received_gbp)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Total Tax</p>
                  <p className="text-xl font-bold text-rose-light mt-1">
                    {fmtGBP(blend.first_retire_year.total_tax_gbp)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-text-muted">Effective Rate</p>
                  <p className="text-xl font-bold text-text-primary mt-1">
                    {fmtPct(blend.first_retire_year.effective_tax_rate_pct, 1)}
                  </p>
                </div>
              </div>
            </div>

            {/* Optimal vs Naive comparison */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="card p-4 border-emerald/20 bg-emerald/5">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
                  Optimal Blend
                </p>
                <p className="text-lg font-bold text-emerald-light">
                  {fmtGBP(blend.comparison_vs_naive_all_sipp.optimal_net_gbp)}/yr net
                </p>
                <p className="text-xs text-text-muted mt-1">
                  Tax: {fmtGBP(blend.first_retire_year.total_tax_gbp)}/yr
                </p>
              </div>
              <div className="card p-4 border-rose/20 bg-rose/5">
                <p className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
                  Naive All-SIPP
                </p>
                <p className="text-lg font-bold text-rose-light">
                  {fmtGBP(blend.comparison_vs_naive_all_sipp.naive_net_gbp)}/yr net
                </p>
                <p className="text-xs text-text-muted mt-1">
                  Tax: {fmtGBP(blend.comparison_vs_naive_all_sipp.naive_tax_gbp)}/yr
                </p>
              </div>
            </div>

            {/* Annual saving highlight */}
            {blend.comparison_vs_naive_all_sipp.savings_per_year_gbp > 0 && (
              <div className="card p-3 border-emerald/30 bg-emerald/5 flex items-center gap-3">
                <Zap size={16} className="text-emerald-light shrink-0" aria-hidden />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-text-primary">
                    Save {fmtGBP(blend.comparison_vs_naive_all_sipp.savings_per_year_gbp)}/yr
                    vs all-SIPP strategy
                  </p>
                  <p className="text-xs text-text-muted">
                    {fmtGBP(blend.comparison_vs_naive_all_sipp.savings_per_decade_gbp)} over a decade
                  </p>
                </div>
              </div>
            )}

            {/* Blend sources bar */}
            <div className="card p-4">
              <p className="text-xs font-semibold text-text-primary mb-3">
                Income Sources — Year One (proportional)
              </p>
              {(() => {
                const sources = blend.first_retire_year?.sources
                if (!sources) return null
                const total = Object.values(sources).reduce((s, v) => s + v, 0)
                const keys = Object.keys(sources) as (keyof BlendSources)[]
                return (
                  <div className="space-y-3">
                    {/* Stacked bar */}
                    <div className="flex h-5 rounded overflow-hidden w-full">
                      {keys
                        .filter(k => sources[k] > 0)
                        .map(k => (
                          <div
                            key={k}
                            className={clsx('h-full', SOURCE_COLOURS[k])}
                            style={{ width: `${(sources[k] / total) * 100}%` }}
                            title={`${SOURCE_LABELS[k]}: ${fmtGBP(sources[k])}`}
                          />
                        ))}
                    </div>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {keys
                        .filter(k => sources[k] > 0)
                        .map(k => (
                          <div key={k} className="flex items-center gap-1.5 text-xs">
                            <div className={clsx('w-3 h-3 rounded-sm shrink-0', SOURCE_COLOURS[k])} />
                            <span className="text-text-secondary">{SOURCE_LABELS[k]}</span>
                            <span className="font-mono text-text-primary">{fmtGBP(sources[k])}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )
              })()}
            </div>

          </div>
        )}

        {/* Save button — available whether or not a blend year exists */}
        {blend && (
          <div className="mt-4 space-y-2">
            {saveTargetError && (
              <div className="card p-3 border-rose/30 bg-rose/5 text-xs text-rose-light">
                {saveTargetError}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={saveHouseholdTarget}
                disabled={savingTarget || savedTarget}
                className={clsx(
                  'btn-secondary text-xs gap-2',
                  savedTarget && 'text-emerald-light border-emerald/30',
                )}
              >
                {savedTarget ? 'Saved!' : savingTarget ? 'Saving…' : 'Save as household target'}
              </button>
            </div>
          </div>
        )}
      </section>
      {/* ─────────────────────────────────────────────────────────────────── */}

      {/* ── Full 35-Year Projection ──────────────────────────────────────── */}
      {blend && blend.projection.length > 0 && (
        <details className="card p-0 overflow-hidden">
          <summary className="cursor-pointer select-none px-5 py-4 flex items-center gap-2 text-sm font-semibold text-text-primary hover:bg-surface-card/50 transition-colors">
            <TrendingUp size={14} className="text-emerald-light shrink-0" aria-hidden />
            Full 35-Year Projection
            <span className="ml-auto text-[10px] font-normal text-text-muted uppercase tracking-wider">
              click to expand
            </span>
          </summary>

          <div className="px-5 pb-6 pt-2 space-y-6">
            {/* ── Stacked area chart ─────────────────────────────────────── */}
            <div>
              <p className="text-xs text-text-muted mb-3">Portfolio balance by wrapper over time</p>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={blend.projection} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis
                    dataKey="age"
                    tick={{ fontSize: 10, fill: axisTickFill }}
                    tickLine={false}
                    axisLine={false}
                    label={{ value: 'Age', position: 'insideBottom', offset: -2, fontSize: 10, fill: axisTickFill }}
                  />
                  <YAxis
                    tickFormatter={fmtGBPCompact}
                    tick={{ fontSize: 10, fill: axisTickFill }}
                    tickLine={false}
                    axisLine={false}
                    width={52}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => [fmtGBPCompact(value), name]}
                    labelFormatter={(label) => `Age ${label}`}
                    contentStyle={tooltipContentStyle}
                  />
                  <Legend
                    wrapperStyle={legendWrapperStyle}
                    formatter={(value) => <span className="text-text-secondary">{value}</span>}
                  />
                  <ReferenceLine
                    x={retireAge}
                    stroke="rgba(251,191,36,0.5)"
                    strokeDasharray="4 3"
                    label={{ value: 'Retire', position: 'top', fontSize: 10, fill: 'rgba(251,191,36,0.8)' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sipp_balance_gbp"
                    name="SIPP"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey="isa_balance_gbp"
                    name="ISA"
                    stackId="1"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey="gia_balance_gbp"
                    name="GIA"
                    stackId="1"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.6}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* ── Year-by-year table ─────────────────────────────────────── */}
            <div>
              <p className="text-xs text-text-muted mb-2">Year-by-year detail</p>
              <div className="overflow-auto rounded-xl border border-border max-h-[400px]">
                <table className="w-full text-xs border-collapse min-w-[720px] table-pro">
                  <thead>
                    <tr>
                      <th>Yr</th>
                      <th>Age</th>
                      <th>Phase</th>
                      <th className="!text-right">Gross</th>
                      <th className="!text-right">Net</th>
                      <th className="!text-right">Tax</th>
                      <th className="!text-right">Eff %</th>
                      <th className="!text-right">SIPP</th>
                      <th className="!text-right">ISA</th>
                      <th className="!text-right">GIA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blend.projection.map((row, idx) => {
                      const total = row.sipp_balance_gbp + row.isa_balance_gbp + row.gia_balance_gbp
                      const isFirstDrawdown =
                        row.phase === 'drawdown' &&
                        (idx === 0 || blend.projection[idx - 1].phase !== 'drawdown')
                      const isLowBalance = total < 1_000_000 && total > 0
                      return (
                        <tr
                          key={row.year}
                          className={clsx(
                            'border-b border-border/40 transition-colors',
                            isFirstDrawdown && 'bg-emerald/10',
                            isLowBalance && !isFirstDrawdown && 'bg-rose/10',
                            !isFirstDrawdown && !isLowBalance && 'hover:bg-surface-card/40',
                          )}
                        >
                          <td className="text-text-muted font-mono">{row.year}</td>
                          <td className="font-semibold text-text-primary">{row.age}</td>
                          <td>
                            <span className={clsx(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                              row.phase === 'drawdown'
                                ? 'bg-amber/20 text-amber-300'
                                : 'bg-blue/20 text-blue-300',
                            )}>
                              {row.phase === 'drawdown' ? 'Drawdown' : 'Accum.'}
                            </span>
                          </td>
                          {row.phase === 'drawdown' ? (
                            <>
                              <td className="text-right font-mono text-text-primary">{fmtGBPCompact(row.gross_received_gbp)}</td>
                              <td className="text-right font-mono text-emerald-light">{fmtGBPCompact(row.net_received_gbp)}</td>
                              <td className="text-right font-mono text-rose-light">{fmtGBPCompact(row.total_tax_gbp)}</td>
                              <td className="text-right font-mono text-text-muted">{fmtPct(row.effective_tax_rate_pct, 1)}</td>
                            </>
                          ) : (
                            <>
                              <td className="text-right text-text-muted">—</td>
                              <td className="text-right text-text-muted">—</td>
                              <td className="text-right text-text-muted">—</td>
                              <td className="text-right text-text-muted">—</td>
                            </>
                          )}
                          <td className="text-right font-mono text-blue-300">{fmtGBPCompact(row.sipp_balance_gbp)}</td>
                          <td className="text-right font-mono text-emerald-light">{fmtGBPCompact(row.isa_balance_gbp)}</td>
                          <td className="text-right font-mono text-amber-300">{fmtGBPCompact(row.gia_balance_gbp)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Sustainability verdict ─────────────────────────────────── */}
            {(() => {
              const last = blend.projection[blend.projection.length - 1]
              const finalTotal = last.sipp_balance_gbp + last.isa_balance_gbp + last.gia_balance_gbp
              const lifetimeTax = blend.projection
                .filter(r => r.phase === 'drawdown')
                .reduce((sum, r) => sum + r.total_tax_gbp, 0)

              // Find first year where total drops to zero (or below)
              const depletedRow = blend.projection.find(
                r => r.sipp_balance_gbp + r.isa_balance_gbp + r.gia_balance_gbp <= 0,
              )

              const sustainable = finalTotal > 0

              return (
                <div className={clsx(
                  'card p-5 border-2',
                  sustainable ? 'border-emerald/30 bg-emerald/5' : 'border-rose/30 bg-rose/5',
                )}>
                  <div className="flex items-start gap-3">
                    {sustainable
                      ? <ShieldCheck size={18} className="text-emerald-light shrink-0 mt-0.5" aria-hidden />
                      : <AlertTriangle size={18} className="text-rose-light shrink-0 mt-0.5" aria-hidden />
                    }
                    <div className="flex-1 min-w-0">
                      <p className={clsx(
                        'text-sm font-bold',
                        sustainable ? 'text-emerald-light' : 'text-rose-light',
                      )}>
                        {sustainable
                          ? `Sustainable — portfolio survives to age ${last.age} with ${fmtGBP(finalTotal)} remaining`
                          : `Portfolio depleted at age ${depletedRow?.age ?? last.age} — ${95 - (depletedRow?.age ?? last.age)} year${(95 - (depletedRow?.age ?? last.age)) !== 1 ? 's' : ''} of shortfall`
                        }
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        Total lifetime tax across all drawdown years:{' '}
                        <span className="text-text-secondary font-semibold">{fmtGBP(lifetimeTax)}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        </details>
      )}
      {/* ─────────────────────────────────────────────────────────────────── */}

      {/* Headline */}
      {data && (
        <div className="card p-6 border-2 border-blue/30 bg-blue/5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="panel-title text-blue-light">
                DRAWDOWN PLAN
              </p>
              <h2 className="text-2xl font-bold text-text-primary mt-1">{data.headline}</h2>
              <div className="flex items-center gap-6 mt-4 flex-wrap text-sm">
                <div>
                  <span className="text-text-muted">Current pot: </span>
                  <span className="font-bold text-text-primary">{fmtGBP(data.current_pot_gbp)}</span>
                </div>
                <div>
                  <span className="text-text-muted">Years to retirement: </span>
                  <span className="font-bold text-text-primary">{data.years_to_retirement}</span>
                </div>
                <div>
                  <span className="text-text-muted">Years in retirement: </span>
                  <span className="font-bold text-text-primary">{data.years_in_retirement}</span>
                </div>
                <div>
                  <span className="text-text-muted">Lifetime spend need: </span>
                  <span className="font-bold text-text-primary">{fmtGBP(data.total_lifetime_spend_needed_gbp)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FX Stress impact banner — only shown when stress is active */}
      {data?.fx_stress && data.fx_stress.applied_pct !== 0 && (
        <div className={clsx(
          'card p-4 border',
          (data.fx_stress.impact_gbp ?? 0) < 0
            ? 'border-rose/30 bg-rose/5'
            : 'border-emerald/30 bg-emerald/5',
        )}>
          <div className="flex items-start gap-3">
            <Globe
              size={16}
              className={clsx(
                'shrink-0 mt-0.5',
                (data.fx_stress.impact_gbp ?? 0) < 0 ? 'text-rose-light' : 'text-emerald-light',
              )}
              aria-hidden
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-text-primary mb-1">
                FX Stress Active — GBP {data.fx_stress.applied_pct > 0 ? '+' : ''}{data.fx_stress.applied_pct}% (sustained)
              </p>
              {data.fx_stress.error ? (
                <p className="text-xs text-rose-light">{data.fx_stress.error}</p>
              ) : (
                <>
                  <p className="text-xs text-text-secondary">
                    {data.fx_stress.note}
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs">
                    <span className="text-text-muted">
                      Pot before:{' '}
                      <span className="font-semibold text-text-primary">
                        {fmtGBP(data.fx_stress.pot_before)}
                      </span>
                    </span>
                    <span className="text-text-muted">
                      Pot after:{' '}
                      <span className="font-semibold text-text-primary">
                        {fmtGBP(data.fx_stress.pot_after)}
                      </span>
                    </span>
                    <span className="text-text-muted">
                      Impact:{' '}
                      <span className={clsx(
                        'font-semibold',
                        (data.fx_stress.impact_gbp ?? 0) < 0 ? 'text-rose-light' : 'text-emerald-light',
                      )}>
                        {(data.fx_stress.impact_gbp ?? 0) >= 0 ? '+' : ''}
                        {fmtGBP(data.fx_stress.impact_gbp)}
                      </span>
                    </span>
                    {data.fx_stress.foreign_share != null && (
                      <span className="text-text-muted">
                        Foreign share:{' '}
                        <span className="font-semibold text-text-primary">
                          {fmtPct((data.fx_stress.foreign_share ?? NaN) * 100, 1)}
                        </span>
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Inputs
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <EditableNumberSlider
            label="Annual spend (gross)"
            value={spend}
            onChange={(v) => { setSpend(v); patchDebounced({ target_drawdown_gbp: v }) }}
            min={20_000}
            max={400_000}
            step={5_000}
            format={fmtGBP}
          />
          <NumberSlider
            label="Current age"
            value={age}
            onChange={(v) => { setAge(v); patchDebounced({ current_age: v }) }}
            min={25}
            max={85}
            step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="Retirement age"
            value={retireAge}
            onChange={(v) => { setRetireAge(v); patchDebounced({ retirement_age: v }) }}
            min={Math.max(age, 55)}
            max={85}
            step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="State pension age"
            value={statePensionAge}
            onChange={setStatePensionAge}
            min={60}
            max={75}
            step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="Safe Withdrawal Rate"
            value={swr}
            onChange={(v) => { setSwr(v); patchDebounced({ swr_pct: v }) }}
            min={2.5}
            max={5.0}
            step={0.25}
            format={(v) => `${v.toFixed(2)}%`}
          />
          <label className="flex items-center gap-2 text-xs text-text-secondary self-end col-span-2 sm:col-span-1">
            <input
              type="checkbox"
              checked={includeSP}
              onChange={(e) => { setIncludeSP(e.target.checked); patchDebounced({ include_state_pension: e.target.checked }) }}
              className="rounded border-border bg-surface-card"
            />
            {/* Echo the backend's state-pension constant when available so the
                label can never drift from what the engine actually uses. */}
            Include UK state pension (
            {data?.assumptions?.state_pension_gbp_yr != null
              ? fmtGBP(Number(data.assumptions.state_pension_gbp_yr))
              : '£11,973'}
            /yr)
          </label>

          {/* ── FX Stress Presets ── */}
          <div className="col-span-2 sm:col-span-4 lg:col-span-4 pt-1 border-t border-border/40">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Globe size={12} className="text-text-muted" aria-hidden />
                <span className="text-[10px] uppercase tracking-wider text-text-muted">
                  FX Stress — sustained GBP move
                </span>
              </div>
              {fxStressPct !== 0 && (
                <span className={clsx(
                  'text-[10px] font-semibold px-2 py-0.5 rounded-full',
                  fxStressPct > 0
                    ? 'bg-rose/10 text-rose-light'
                    : 'bg-emerald/10 text-emerald-light',
                )}>
                  {fxStressPct > 0 ? 'GBP stronger' : 'GBP weaker'} by {Math.abs(fxStressPct)}%
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {([-15, -5, 0, 5, 15] as const).map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setFxStressPct(pct)}
                  className={clsx(
                    'px-3 py-1 rounded-lg text-xs font-semibold border transition-colors',
                    fxStressPct === pct
                      ? pct > 0
                        ? 'bg-rose/20 border-rose/40 text-rose-light'
                        : pct < 0
                          ? 'bg-emerald/20 border-emerald/40 text-emerald-light'
                          : 'bg-blue/20 border-blue/40 text-blue-light'
                      : 'bg-surface-card border-border text-text-muted hover:border-border/80',
                  )}
                  aria-pressed={fxStressPct === pct ? true : false}
                >
                  {pct === 0 ? 'None' : pct > 0 ? `GBP +${pct}%` : `GBP ${pct}%`}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-text-muted mt-2">
              Applies a first-order adjustment to the foreign-currency share of your
              starting pot. GBP +15% = worst-case scenario for a USD-heavy book.
            </p>
          </div>
        </div>
      </div>

      {/* Bucket cards */}
      {data && (
        <div>
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Wallet size={14} className="text-blue-light" aria-hidden />
            Bucket Strategy
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <BucketCardView bucket={data.bucket_allocation.bucket_1_cash} colour="blue" />
            <BucketCardView bucket={data.bucket_allocation.bucket_2_bonds} colour="amber" />
            <BucketCardView bucket={data.bucket_allocation.bucket_3_growth} colour="emerald" />
          </div>
          <div className="text-[11px] text-text-muted mt-3 flex items-start gap-2">
            <Info size={12} className="mt-0.5 shrink-0" aria-hidden />
            <span>
              Current split — Cash {data.current_bucket_split.cash_pct}% ·
              Bonds {data.current_bucket_split.bond_pct}% ·
              Growth {data.current_bucket_split.growth_pct}%. The buckets decouple
              "when you spend it" from "what the market did last month".
            </span>
          </div>
        </div>
      )}

      {/* Crash test */}
      {data && (
        <div className={clsx(
          'card p-5 border-2',
          data.crash_test.passes ? 'border-emerald/30 bg-emerald/5' : 'border-rose/30 bg-rose/5',
        )}>
          <h2 className="panel-title mb-2 flex items-center gap-2">
            {data.crash_test.passes
              ? <ShieldCheck size={14} className="text-emerald-light" aria-hidden />
              : <AlertTriangle size={14} className="text-rose-light" aria-hidden />
            }
            Crash Test —{' '}
            <span className={data.crash_test.passes ? 'text-emerald-light' : 'text-rose-light'}>
              {data.crash_test.passes ? 'PASS' : 'FAIL'}
            </span>
          </h2>
          <p className="text-xs text-text-secondary mb-3">{data.crash_test.scenario}</p>
          <div className="grid sm:grid-cols-4 gap-3 mb-3 text-xs">
            <div>
              <p className="text-text-muted">Defensive pot</p>
              <p className="text-base font-bold text-text-primary">{fmtGBPCompact(data.crash_test.defensive_pot_gbp)}</p>
            </div>
            <div>
              <p className="text-text-muted">Growth pot (pre)</p>
              <p className="text-base font-bold text-text-primary">{fmtGBPCompact(data.crash_test.growth_pot_pre_crash_gbp)}</p>
            </div>
            <div>
              <p className="text-text-muted">Growth pot (post −30%)</p>
              <p className="text-base font-bold text-rose-light">{fmtGBPCompact(data.crash_test.growth_pot_post_crash_gbp)}</p>
            </div>
            <div>
              <p className="text-text-muted">Defensive coverage</p>
              <p className="text-base font-bold text-text-primary">
                {data.crash_test.years_covered_by_defensive} yrs
                <span className="text-text-muted text-[10px]"> / {data.crash_test.equity_recovery_years_needed} needed</span>
              </p>
            </div>
          </div>
          <p className="text-sm text-text-primary">{data.crash_test.verdict}</p>
        </div>
      )}

      {/* Tax-efficient sequence */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-1 flex items-center gap-2">
            <Shuffle size={14} className="text-blue-light" aria-hidden />
            Tax-Efficient Withdrawal Sequence
          </h2>
          <p className="text-xs text-text-muted mb-4">
            Pull from the tax-noisy pots first so the tax-free pots keep
            compounding longer. CGT on a GIA now saves 40% income tax later.
          </p>
          <ol className="space-y-3">
            {data.tax_efficient_sequence.map((p, i) => (
              <li key={i} className="flex gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-blue/20 border border-blue/40 flex items-center justify-center text-xs font-bold text-blue-light">
                  {p.phase}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-bold text-text-primary">
                        Phase {p.phase} · {p.age_range}
                      </p>
                      <p className="text-xs text-blue-light">{p.source}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-text-muted">Draw</p>
                      <p className="text-sm font-bold text-text-primary">{fmtGBPCompact(p.est_amount_gbp)}</p>
                      <p className="text-[10px] text-text-muted">tax ≈ {fmtGBPCompact(p.est_tax_gbp)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-text-secondary mt-1">{p.why}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Wrapper totals reference */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">Wrapper Totals</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
            {Object.entries(data.wrapper_totals_gbp).map(([k, v]) => (
              <div key={k} className="p-3 rounded-lg border border-border bg-surface-card">
                <p className="text-[10px] uppercase tracking-wider text-text-muted">{k}</p>
                <p className="text-sm font-bold text-text-primary mt-1">{fmtGBPCompact(v)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data && data.recommendations.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <ListChecks size={14} className="text-blue-light" aria-hidden />
            Recommended Actions
          </h2>
          <ul className="space-y-2">
            {data.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-text-secondary flex gap-2">
                <span className="text-blue-light shrink-0">→</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Methodology &amp; UK drawdown rules
        </summary>
        <div className="mt-3 space-y-2">
          <p>
            <span className="text-text-primary font-semibold">Bucket 1 (cash, 2 yrs):</span>{' '}
            instant-access cash and money-market funds. Never has to be sold at a bad price.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Bucket 2 (bonds, 5 yrs):</span>{' '}
            short-dated gilts / investment-grade bond ETFs. Refills Bucket 1 each year.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Bucket 3 (growth):</span>{' '}
            equities, ETFs, and crypto. Long-term compounder that refills Bucket 2.
          </p>
          <p className="pt-2">
            <span className="text-text-primary font-semibold">Tax sequence (UK, April 2026):</span>{' '}
            GIA + Crypto → SIPP 25% tax-free lump sum (LSA £268,275) → SIPP drawdown
            (income tax, kept inside basic-rate band where possible) → ISA last
            (tax-free and IHT-friendly).
          </p>
          <p>
            <span className="text-text-primary font-semibold">State pension:</span>{' '}
            currently £11,973/yr from age 67 — reduces the SIPP draw needed per year
            in phase 2.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Crash test:</span>{' '}
            checks whether Bucket 1 + Bucket 2 combined cover 7 years of spend
            — the typical equity drawdown-to-recovery window. If yes, you can
            ride out a crash at retirement without realising losses on growth
            assets.
          </p>
          <p className="text-text-muted pt-2">
            Real terms throughout (today's pounds). CGT estimate assumes 60% of
            GIA value is unrealised gain. Income tax estimate uses the effective
            marginal rate on state-pension + SIPP drawdown.
          </p>
        </div>
      </details>
      <ScenarioDisclaimerFooter />
    </div>
  )
}

// ── Slider input component (mirror of PlanningPage) ─────────────────────────
function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}

// ── Bucket card ─────────────────────────────────────────────────────────────
function BucketCardView({
  bucket,
  colour,
}: {
  bucket: BucketCard
  colour: 'blue' | 'amber' | 'emerald'
}) {
  const filled = bucket.gap_gbp <= 0
  const barColour =
    colour === 'blue'    ? 'bg-blue'    :
    colour === 'amber'   ? 'bg-amber'   :
                           'bg-emerald'
  const borderColour =
    colour === 'blue'    ? 'border-blue/30 bg-blue/5'       :
    colour === 'amber'   ? 'border-amber/30 bg-amber/5'     :
                           'border-emerald/30 bg-emerald/5'

  return (
    <div className={clsx('card p-5 border-2', borderColour)}>
      <p className="text-[10px] uppercase tracking-wider text-text-muted">
        {bucket.years_covered} years covered
      </p>
      <h3 className="text-sm font-bold text-text-primary mt-1">{bucket.label}</h3>
      <p className="text-[11px] text-text-secondary mt-1">{bucket.purpose}</p>

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-muted">Target</span>
          <span className="font-bold text-text-primary">{fmtGBP(bucket.target_gbp)}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-muted">Current</span>
          <span className="font-bold text-text-primary">{fmtGBP(bucket.current_gbp)}</span>
        </div>
        {!filled && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-text-muted">Gap</span>
            <span className="font-bold text-rose-light">−{fmtGBP(bucket.gap_gbp)}</span>
          </div>
        )}
        {filled && bucket.surplus_gbp > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-text-muted">Surplus</span>
            <span className="font-bold text-emerald-light">+{fmtGBP(bucket.surplus_gbp)}</span>
          </div>
        )}
      </div>

      {/* Fill bar */}
      <div className="mt-4">
        <div className="h-2 rounded-full bg-surface-card overflow-hidden border border-border">
          <div
            className={clsx('h-full transition-all', barColour)}
            style={{ width: `${Math.min(100, bucket.fill_pct)}%` }}
          />
        </div>
        <p className="text-[10px] text-text-muted mt-1 text-right">
          {fmtPct(bucket.fill_pct, 0)} filled
        </p>
      </div>
    </div>
  )
}
